import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Backdrop,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { useTranslation } from "react-i18next";
import { crmService } from "../../services/crm.service";
import { useSnackbar } from "notistack";
import { CopyToClipboard } from "react-copy-to-clipboard";

const PREFIX = "References";

const classes = {
  root: `${PREFIX}-root`,
  imageDesignTitle: `${PREFIX}-imageDesignTitle`,
  referencesContainer: `${PREFIX}-referencesContainer`,
  inputsContainer: `${PREFIX}-inputsContainer`,
  paperStyle: `${PREFIX}-paperStyle`,
  refTitleStyle: `${PREFIX}-refTitleStyle`,
  addButtonStyle: `${PREFIX}-addButtonStyle`,
  iframeContainer: `${PREFIX}-iframeContainer`,
  imgContainer: `${PREFIX}-imgContainer`,
  iframeStyle: `${PREFIX}-iframeStyle`,
  referenceStyle: `${PREFIX}-referenceStyle`,
  specialRefStyle: `${PREFIX}-specialRefStyle`,
  referenceList: `${PREFIX}-referenceList`,
  backdropStyle: `${PREFIX}-backdropStyle`,
  imgStyle: `${PREFIX}-imgStyle`,
  marginTop: `${PREFIX}-marginTop`,
  fillerDiv: `${PREFIX}-fillerDiv`,
  marginRight: `${PREFIX}-marginRight`,
  textContainer: `${PREFIX}-textContainer`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
    },
    [`& .${classes.imageDesignTitle}`]: {
      color: "black",
      fontSize: "24px",
      maxWidth: "650px",
      width: "100%",
      margin: "0 auto",
      textAlign: "center",
      backgroundColor: theme.palette.primary.main,
    },
    [`& .${classes.addButtonStyle}`]: {
      margin: "15px 0",
    },
    [`& .${classes.marginTop}`]: {
      marginTop: "15px",
    },
    [`& .${classes.referencesContainer}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      height: "400px",
    },
    [`& .${classes.inputsContainer}`]: {
      display: "flex",
      flexDirection: "column",
      marginBottom: "2rem",
    },
    [`& .${classes.paperStyle}`]: {
      display: "flex",
      justifyContent: "space-evenly",
      alignItems: "center",
      width: "100%",
      maxWidth: "500px",
    },
    [`& .${classes.specialRefStyle}`]: {
      width: "160px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    [`& .${classes.refTitleStyle}`]: {
      width: "136px",
      maxHeight: "20px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    [`& .${classes.referenceList}`]: {
      maxHeight: "500px",
      overflowY: "auto",
      width: "100%",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.referenceStyle}`]: {
      display: "flex",
      justifyContent: "space-around",
      alignItems: "center",
      width: "100%",
      marginBottom: "10px",
    },
    [`& .${classes.backdropStyle}`]: {
      color: "#D3AF5F",
      zIndex: "15",
    },
    [`& .${classes.fillerDiv}`]: {
      width: "40px",
      height: "40px",
    },
    [`& .${classes.marginRight}`]: {
      marginRight: "5px",
    },
    [`& .${classes.textContainer}`]: {
      display: "flex",
      alignItems: "center",
    },
  };
});

const StyledPreview = styled("div")(({ theme }) => {
  return {
    [`& .${classes.iframeContainer}`]: {
      display: "flex",
      justifyContent: "center",
      height: "100%",
      maxWidth: "650px",
      margin: "2rem auto",
    },
    [`& .${classes.imgContainer}`]: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      marginTop: "1rem",
    },
    [`& .${classes.imgStyle}`]: {
      width: "100%",
      maxWidth: "650px",
      [theme.breakpoints.between("xs", "sm")]: {
        maxWidth: "350px",
      },
    },
    [`& .${classes.iframeStyle}`]: {
      height: "85vh",
      overflow: "auto",
    },
  };
});

export default function References({ data, setData, canUserModifyData }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [previewModal, setPreviewModal] = useState(null);
  const [file, setFile] = useState([]);
  const [isUrl, setIsUrl] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [titleExist, setTitleExist] = useState("");
  const [urlTitle, seturlTitle] = useState("");
  const [indexMockup, setIndexMockup] = useState();
  const [indexVideoRef, setIndexVideoRef] = useState();
  const myBlob = useRef();

  useEffect(() => {
    setIndexMockup(data.references.map((ref) => ref.title).indexOf("Mockup"));
    setIndexVideoRef(
      data.references.map((ref) => ref.title).indexOf("Vidéo simulation")
    );
  }, [data.references]);

  useEffect(() => {
    if (!file || titleExist === "") {
      return;
    }

    const tmpFileObject = new File([file[0]], file[0].name, {
      type: file[0].type,
    });

    const tmp = [];
    tmp.push(tmpFileObject);

    setOpenBackdrop(true);
    crmService
      .uploadReference(data._id, tmp[0], titleExist)
      .then((res) => {
        setData((draft) => {
          draft.references.push(res);
        });
        enqueueSnackbar(t("Crm.toasts.uploadSuccess"), { variant: "success" });
        setOpenBackdrop(false);
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("Crm.toasts.uploadFail"), { variant: "error" });
        setOpenBackdrop(false);
      });
    setTitleExist("");
  }, [file]);

  useEffect(() => {
    if (/^(http|https):\/\//.test(titleExist))
      //
      setIsUrl(true);
    else setIsUrl(false);
  }, [titleExist]);

  function handleDisable() {
    if (
      titleExist === "" ||
      titleExist === "Mockup" ||
      titleExist === "Vidéo simulation"
    )
      return true;
    else return false;
  }

  const handleLinkUpload = () => {
    crmService
      .createLink(data._id, titleExist, urlTitle)
      .then((res) => {
        setData((draft) => {
          draft.references.push(res);
        });
        enqueueSnackbar(t("Crm.toasts.uploadSuccess"), { variant: "success" });
        setOpenBackdrop(false);
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("Crm.toasts.uploadFail"), { variant: "error" });
        setOpenBackdrop(false);
      });
    setTitleExist("");
  };

  function handlePreviewModal(item) {
    setOpenBackdrop(true);
    crmService
      .getReference(data._id, item.id)
      .then((res) => {
        myBlob.current = res;
        if (res.type === "application/octet-stream") {
          myBlob.current = new Blob([res], { type: "video/mp4" });
        }
        setPreviewModal(URL.createObjectURL(myBlob.current));
        setOpenBackdrop(false);
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("Crm.toasts.previewFail"), { variant: "error" });
        setOpenBackdrop(false);
      });
  }

  function handleFileChange(event) {
    if (event.target.files) {
      setFile([...event.target.files]);
    }
  }

  function handleDeleteElement(id, index, type = "ref") {
    setOpenBackdrop(true);
    if (type === "link") {
      crmService
        .deleteLink(data._id, id)
        .then(() => {
          setData((data) => {
            data.references.splice(index, 1);
          });
          enqueueSnackbar(t("Crm.toasts.deleteSuccess"), {
            variant: "success",
          });
          setOpenBackdrop(false);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("Crm.toasts.deleteFail"), { variant: "error" });
          setOpenBackdrop(false);
        });
    } else {
      crmService
        .deleteReference(data._id, id)
        .then(() => {
          setData((data) => {
            data.references.splice(index, 1);
          });
          enqueueSnackbar(t("Crm.toasts.deleteSuccess"), {
            variant: "success",
          });
          setOpenBackdrop(false);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("Crm.toasts.deleteFail"), { variant: "error" });
          setOpenBackdrop(false);
        });
    }
  }

  function handleDownload(item) {
    var a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    setOpenBackdrop(true);
    crmService
      .getReference(data._id, item.id)
      .then((res) => {
        const url = window.URL.createObjectURL(res);
        a.href = url;
        a.download = item.title;
        a.click();
        window.URL.revokeObjectURL(url);
        setOpenBackdrop(false);
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("Crm.toasts.downloadFail"), { variant: "error" });
        setOpenBackdrop(false);
      });
  }

  return (
    <StyledDiv>
      <ResponsiveDialogManager
        title={t("Crm.preview")}
        fullScreen
        open={previewModal ? true : false}
        onClose={() => {
          myBlob.current = null;
          setPreviewModal(null);
        }}
        renderer={
          <StyledPreview>
            {myBlob.current?.type.startsWith("image/") ? (
              <div className={classes.imgContainer}>
                <img
                  className={classes.imgStyle}
                  src={previewModal}
                  alt="preview Crm"
                />
              </div>
            ) : (
              <div className={classes.iframeContainer}>
                <iframe
                  className={classes.iframeStyle}
                  title="Preview"
                  width="100%"
                  height="100%"
                  src={previewModal}
                ></iframe>
              </div>
            )}
          </StyledPreview>
        }
      />
      <Backdrop className={classes.backdropStyle} open={openBackdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className={classes.root}>
        <div className={classes.referencesContainer}>
          <div className={classes.inputsContainer}>
            <TextField
              variant="outlined"
              disabled={!canUserModifyData}
              label={t("Crm.referenceName")}
              value={titleExist ? titleExist : ""}
              onChange={(e) => {
                setTitleExist(e.target.value);
              }}
            />
            {handleDisable() && titleExist.length > 0 && (
              <Typography variant="caption">{t("Crm.nameRule")}</Typography>
            )}
            {isUrl ? (
              <>
                <TextField
                  variant="outlined"
                  className={classes.marginTop}
                  label={t("Crm.linkName")}
                  value={urlTitle ? urlTitle : ""}
                  onChange={(e) => {
                    seturlTitle(e.target.value);
                  }}
                />
                <Button
                  className={classes.addButtonStyle}
                  variant="outlined"
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  size="large"
                  id="add-link-button"
                  color="primary"
                  onClick={(e) => {
                    handleLinkUpload();
                  }}
                  disabled={urlTitle ? false : true}
                >
                  {t("Crm.addLink")}
                </Button>
              </>
            ) : (
              <Button
                component="label"
                className={classes.addButtonStyle}
                variant="outlined"
                startIcon={<AddCircleOutlineOutlinedIcon />}
                size="large"
                id="add-reference-button"
                color="primary"
                onChange={(e) => {
                  handleFileChange(e);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                disabled={handleDisable() || !canUserModifyData}
              >
                {t("Crm.addReference")}
                <input type="file" hidden />
              </Button>
            )}
          </div>
          <div className={classes.referenceList}>
            <div className={classes.referenceStyle}>
              <Paper elevation={9} className={classes.paperStyle}>
                <Typography variant="h5" className={classes.specialRefStyle}>
                  Mockup
                </Typography>
                {indexMockup >= 0 ? (
                  <>
                    <IconButton
                      color="primary"
                      aria-label="preview"
                      onClick={() =>
                        handlePreviewModal(data.references[indexMockup])
                      }
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        handleDownload(data.references[indexMockup])
                      }
                      aria-label="download"
                    >
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      disabled={!canUserModifyData}
                      color="primary"
                      onClick={() =>
                        handleDeleteElement(
                          data.references[indexMockup].id,
                          indexMockup
                        )
                      }
                    >
                      <CloseOutlinedIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton
                      aria-label="add"
                      component="label"
                      color="primary"
                      disabled={!canUserModifyData}
                      onChange={(e) => {
                        setTitleExist("Mockup");
                        handleFileChange(e);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <input type="file" hidden />
                      <AddCircleOutlineOutlinedIcon />
                    </IconButton>
                  </>
                )}
              </Paper>
            </div>
            <div className={classes.referenceStyle}>
              <Paper elevation={9} className={classes.paperStyle}>
                <Typography variant="h5" className={classes.specialRefStyle}>
                  {t("Crm.videoSimulation")}
                </Typography>
                {indexVideoRef >= 0 ? (
                  <>
                    <IconButton
                      aria-label="preview"
                      color="primary"
                      onClick={() =>
                        handlePreviewModal(data.references[indexVideoRef])
                      }
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                    <IconButton
                      onClick={() =>
                        handleDownload(data.references[indexVideoRef])
                      }
                      color="primary"
                      aria-label="download"
                    >
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                    <CopyToClipboard text={data.references[indexVideoRef].url}>
                      <IconButton
                        color="primary"
                        onClick={(e) => {
                          enqueueSnackbar(t("toasts.copiedInClipboard"), {
                            variant: "info",
                          });
                          e.stopPropagation();
                        }}
                      >
                        <InsertLinkIcon />
                      </IconButton>
                    </CopyToClipboard>
                    <IconButton
                      aria-label="delete"
                      color="primary"
                      disabled={!canUserModifyData}
                      onClick={() =>
                        handleDeleteElement(
                          data.references[indexVideoRef].id,
                          indexVideoRef
                        )
                      }
                    >
                      <CloseOutlinedIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton
                      aria-label="add"
                      component="label"
                      color="primary"
                      disabled={!canUserModifyData}
                      onChange={(e) => {
                        setTitleExist("Vidéo simulation");
                        handleFileChange(e);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <input type="file" hidden />
                      <AddCircleOutlineOutlinedIcon />
                    </IconButton>
                  </>
                )}
              </Paper>
            </div>
            {data.references.map((item, index) => {
              if (item.title !== "Mockup" && item.title !== "Vidéo simulation")
                return (
                  <div
                    className={classes.referenceStyle}
                    key={`key-${index + 1}`}
                  >
                    <Paper elevation={20} className={classes.paperStyle}>
                      {item.link ? (
                        <>
                          <div className={classes.textContainer}>
                            <InsertLinkIcon className={classes.marginRight} />
                            <Typography
                              variant="body2"
                              className={classes.refTitleStyle}
                            >
                              {item.title}
                            </Typography>
                          </div>
                          <div className={classes.fillerDiv}></div>
                          <Link
                            href={item.link}
                            underline="none"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton aria-label="open-link" color="primary">
                              <OpenInNewIcon />
                            </IconButton>
                          </Link>
                          <IconButton
                            color="primary"
                            aria-label="delete"
                            disabled={!canUserModifyData}
                            onClick={() =>
                              handleDeleteElement(item.id, index, "link")
                            }
                          >
                            <CloseOutlinedIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <div className={classes.textContainer}>
                            <InsertDriveFileIcon />
                            <Typography
                              variant="body2"
                              className={classes.refTitleStyle}
                            >
                              {typeof item === "string" ? item : item.title}
                            </Typography>
                          </div>
                          <IconButton
                            aria-label="preview"
                            color="primary"
                            onClick={() => handlePreviewModal(item)}
                          >
                            <VisibilityOutlinedIcon />
                          </IconButton>
                          <IconButton
                            color="primary"
                            onClick={() => handleDownload(item)}
                            aria-label="download"
                          >
                            <FileDownloadOutlinedIcon />
                          </IconButton>
                          <IconButton
                            color="primary"
                            aria-label="delete"
                            disabled={!canUserModifyData}
                            onClick={() => handleDeleteElement(item.id, index)}
                          >
                            <CloseOutlinedIcon />
                          </IconButton>
                        </>
                      )}
                    </Paper>
                  </div>
                );
              else return null;
            })}
          </div>
        </div>
      </div>
    </StyledDiv>
  );
}
